import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Container,
  FormFeedback,
} from "reactstrap"
import { useNavigate, useParams } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useSelector, useDispatch } from "react-redux"

import Breadcrumbs from "../../../components/Common/Breadcrumb"
import SuccessPopup from "../../../components/SuccessPopup"
import ErrorPopup from "../../../components/ErrorPopup"
import {
  updatePvsKeyword,
  resetPvsKeywordState,
  fetchPvsKeywordDetails,
} from "../../../redux/actions/pvs"

function EditKeyword() {
  document.title = "Edit PVS Keyword | PVS | Admin Dashboard"

  const history = useNavigate()
  const dispatch = useDispatch()
  const { enc_pvs_key_id } = useParams()

  const updatePvsKeywordScreen = useSelector(
    state => state.screens.pvs.updatePvsKeyword
  )
  const fetchPvsReducer = useSelector(state => state.pvs)

  const [successOpen, setSuccessOpen] = useState(false)
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      keyword: "",
      description: "",
    },
    validationSchema: Yup.object({
      keyword: Yup.string().required("Please Enter Keyword"),
      // description: Yup.string().required("Please Enter Description"),
    }),
    onSubmit: values => {
      const keywordData = {
        keyword: values.keyword,
        description: values.description,
        enc_pvs_key_id,
      }
      dispatch(updatePvsKeyword(keywordData))
    },
  })

  useEffect(() => {
    if (updatePvsKeywordScreen.success) {
      setSuccessOpen(true)
    } else if (updatePvsKeywordScreen.error) {
      setErrorMsg(updatePvsKeywordScreen.message)
      setErrorOpen(true)
    }
  }, [updatePvsKeywordScreen])

  // Fetch category data on mount using Redux dispatch
  useEffect(() => {
    if (!enc_pvs_key_id) return
    const payload = { enc_pvs_key_id }
    dispatch(fetchPvsKeywordDetails(payload))
  }, [enc_pvs_key_id])

  // Set form values from Redux state
  useEffect(() => {
    if (fetchPvsReducer && fetchPvsReducer.keywordsDetail) {
      const data = fetchPvsReducer.keywordsDetail
      validation.setValues({
        keyword: data.keyword || "",
        slug: data.slug || "",
        count: data.count || 0,
        description: data.description || "",
      })
    } else if (fetchPvsReducer && fetchPvsReducer.error) {
      setErrorMsg(fetchPvsReducer.message || "Unable to fetch keyword data.")
      setErrorOpen(true)
    }
  }, [fetchPvsReducer])

  return (
    <React.Fragment>
      <SuccessPopup
        isOpen={successOpen}
        toggle={() => setSuccessOpen(false)}
        onConfirm={() => {
          setSuccessOpen(false)
          dispatch(resetPvsKeywordState())
          history("/pvs-keywords", { replace: true })
        }}
        message={updatePvsKeywordScreen.message}
      />
      <ErrorPopup
        isOpen={errorOpen}
        toggle={() => {
          setErrorOpen(false)
          dispatch(resetPvsKeywordState())
        }}
        message={errorMsg}
      />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            maintitle="PVS"
            title="Keyword"
            breadcrumbItem="Edit Keyword"
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Form
                    className="mt-4"
                    onSubmit={e => {
                      e.preventDefault()
                      validation.handleSubmit()
                      return false
                    }}
                    action="#"
                  >
                    <div className="row">
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="keyword">
                          Keyword
                        </Label>
                        <Input
                          name="keyword"
                          className="form-control"
                          placeholder="Enter keyword"
                          type="text"
                          id="keyword"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.keyword || ""}
                          invalid={
                            validation.touched.keyword &&
                            validation.errors.keyword
                              ? true
                              : false
                          }
                        />
                        {validation.touched.keyword &&
                        validation.errors.keyword ? (
                          <FormFeedback type="invalid">
                            {validation.errors.keyword}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="keyword-slug">
                          Keyword Slug
                        </Label>
                        <Input
                          name="keyword-slug"
                          className="form-control"
                          placeholder="Enter keyword slug"
                          id="keyword-slug"
                          value={validation.values.slug || ""}
                          disabled
                        />
                      </div>
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="keyword-count">
                          Keyword Count
                        </Label>
                        <Input
                          name="keyword-count"
                          className="form-control"
                          placeholder="Enter keyword count"
                          id="keyword-count"
                          value={validation.values.count || 0}
                          disabled
                        />
                      </div>
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="description">
                          Description
                        </Label>
                        <textarea
                          name="description"
                          id="description"
                          className={`form-control ${
                            validation.touched.description &&
                            validation.errors.description
                              ? "is-invalid"
                              : ""
                          }`}
                          rows="2"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.description || ""}
                        />
                        {validation.touched.description &&
                        validation.errors.description ? (
                          <FormFeedback type="invalid">
                            {validation.errors.description}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </div>
                    <div className="d-flex justify-content-center mt-3">
                      <Button
                        type="submit"
                        color="primary"
                        size="lg"
                        className="mt-3"
                      >
                        Submit
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default EditKeyword
